import type { DeliveryVendor } from './delivery-sync';

/**
 * Delivery-tab vendor form (ported from cpanel
 * `features/clients/model/transforms.ts` + the delivery parts of
 * `features/merchants/model/merchantForm.ts` — logic 1:1).
 *
 * The Доставка tab edits the delivery-service vendor mirror: merchant-owned
 * fields stay disabled and synced, business identity (images, business name,
 * address, INN) lives on the Merchant tab and never rides this payload, and
 * the payload mapping keeps its quirks (phone stripping, the <13
 * business_number gate, last-image avatar/logo).
 */

/** Form values of the delivery vendor form (old `ClientFormValues`). */
export interface ClientFormValues {
  first_name?: string;
  last_name?: string;
  preferred_lang?: string;
  phone_number?: string;
  email?: string;
  password?: string;
  min_balance?: string | number;
  search_time?: string | number;
  restrictions?: string;
  business_number?: string;
  address?: string;
  contract_number?: string;
  contract_start_date?: string | Date | undefined;
  buisness_desc?: string;
  business_name?: string;
  /**
   * Vendor `legal_form` enum mirrored into the form (merchant-owned, synced
   * from the profile like the other identity fields). It drives no UI and
   * never rides the payload.
   */
  legal_form?: string;
  /**
   * Payment method radio: balance (`true`) or cash (`false`). Hydrated from
   * the vendor row and sent back on save.
   */
  is_corporate?: boolean;
  is_business?: boolean;
  door_to_door_required?: boolean;
  allow_multiple_orders?: boolean;
  is_client_full_pay?: boolean;
  inn?: string;
  official_business_name?: string;
  /** Webhook (callback) settings — edited in the tab, sent back on save. */
  callback_url?: string;
  callback_token?: string;
  avatar_image?: { path?: string; key?: string; sequence_number?: number }[];
  business_image?: { path?: string; key?: string; sequence_number?: number }[];
  clientType?: string;
}

/**
 * Merchant-owned delivery fields: disabled in the tab, synced from the
 * profile. Business identity (`business_name`, `address`, images, `inn`)
 * lives on the Merchant tab and is intentionally absent — it never renders
 * here.
 */
export const MERCHANT_OWNED_DELIVERY_FIELDS = [
  'first_name',
  'legal_form',
  'phone_number',
  'business_number',
];

/** Never rendered in the delivery tab (values still ride the payload). */
export const HIDDEN_DELIVERY_FIELDS = ['password', 'phone_number', 'business_number'];

export const getDeliveryVendorBusinessFlag = (
  vendor?: Pick<DeliveryVendor, 'is_business' | 'legal_form'>,
): boolean =>
  Boolean(vendor?.is_business) && vendor?.legal_form !== 'LEGAL_FORM_INDIVIDUAL';

/** Backend zero-date sentinel meaning "no contract start set". */
const UNSET_CONTRACT_START_DATE = '0001-01-01T00:00:00Z';

/**
 * Form shows the balance magnitude (0..Infinity); the backend stores it
 * negated (see `toPayloadMinimalBalance`).
 */
function toFormMinBalance(value: string | number | null | undefined): string | number {
  if (value === '' || value === null || value === undefined) return '';
  const parsed = Number(value);
  return Number.isNaN(parsed) ? '' : Math.abs(parsed);
}

/**
 * Payload transform: the filled positive value is sent negated, i.e.
 * `1 000 000` becomes `-1 000 000`. Absolute first so a stray negative
 * stays negative instead of flipping positive.
 */
function toPayloadMinimalBalance(value: string | number | null | undefined): number {
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return NaN;
  return parsed === 0 ? 0 : -Math.abs(parsed);
}

/**
 * Contract-start value for the delivery form: the vendor's date when set,
 * otherwise the already-picked date, otherwise now (so a fresh vendor in
 * create mode defaults to today instead of an empty picker).
 */
export function defaultContractStartDate(
  vendorDate: string | null | undefined,
  current: Date | string | undefined,
): Date {
  if (vendorDate && vendorDate !== UNSET_CONTRACT_START_DATE) {
    return new Date(vendorDate);
  }
  return current instanceof Date ? current : new Date();
}

export const createDeliveryFormDefaultsFromVendor = (
  vendor?: DeliveryVendor,
): ClientFormValues => {
  const avatar = vendor?.logo
    ? [{ path: vendor.logo, key: vendor.logo, sequence_number: 1 }]
    : [];
  const businessImage = vendor?.business_logo ?? vendor?.logo;
  const contractStartDate =
    vendor?.contract_start_date &&
    vendor.contract_start_date !== UNSET_CONTRACT_START_DATE
      ? new Date(vendor.contract_start_date)
      : undefined;

  return {
    clientType: 'vendor',
    first_name: vendor?.name ?? '',
    is_business: getDeliveryVendorBusinessFlag(vendor),
    is_corporate: Boolean(vendor?.is_corporate ?? false),
    // A null business name stays empty — it never falls back to the vendor
    // name or any other value.
    business_name: vendor?.business_name ?? '',
    last_name: '',
    phone_number: vendor?.phone ?? '',
    business_number: vendor?.business_number ?? '',
    preferred_lang: vendor?.preferred_lang ?? 'uz',
    email: vendor?.email ?? '',
    avatar_image: avatar,
    business_image: businessImage
      ? [{ path: businessImage, key: businessImage, sequence_number: 1 }]
      : [],
    buisness_desc: vendor?.business_desc ?? '',
    address: vendor?.address ?? '',
    min_balance: toFormMinBalance(vendor?.minimal_balance),
    search_time: vendor?.search_time ?? '',
    contract_number: vendor?.contract_number ?? '',
    restrictions: String(vendor?.enable_balance_restrictions ?? false),
    door_to_door_required: Boolean(vendor?.door_to_door_required ?? true),
    allow_multiple_orders: Boolean(vendor?.allow_multiple_orders ?? false),
    is_client_full_pay: Boolean(vendor?.is_client_full_pay),
    legal_form: vendor?.legal_form ?? '',
    inn: vendor?.inn ?? '',
    official_business_name:
      vendor?.official_business_name ?? vendor?.business_name ?? vendor?.name ?? '',
    contract_start_date: contractStartDate,
    callback_url: vendor?.callback_url ?? vendor?.callback?.url ?? '',
    callback_token: vendor?.callback_token ?? vendor?.callback?.token ?? '',
  };
};

const stripPhone = (phone?: string): string => `${phone?.replace(/[X() ]/g, '')}`;

/**
 * Trims an optional string for the wire; an empty (or missing) value means
 * "unset" — sent as `null` instead of `""` so the backend clears the value
 * rather than storing an empty string.
 */
function toNullableString(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

const businessNumber = (value?: string): string =>
  value && value.length < 13 ? '' : (value?.replace(/[X() ]/g, '') ?? '');

/**
 * Payload keys the delivery tab never sends, as API keys: merchant-owned
 * controls (disabled in the tab, synced from the profile) plus business
 * identity (images, business name, address, INN) — those live on the Merchant
 * tab now and are saved with the merchant payload instead.
 */
const DISABLED_VENDOR_UPDATE_KEYS = [
  'name',
  'legal_form',
  'phone',
  'business_number',
  'inn',
  'business_name',
  'business_logo',
  'logo',
  'address',
];

/** Payload keys for controls the tab never renders, as API keys. */
const ALWAYS_HIDDEN_VENDOR_UPDATE_KEYS = ['password', 'phone', 'business_number'];

/**
 * Payload keys hidden by the tab's conditional sections for the CURRENT form
 * state (parity with `DeliveryVendorFields`): only the balance section gates
 * on its own toggle. Nothing gates on `legal_form` anymore.
 */
function hiddenVendorUpdateKeys(values: ClientFormValues): string[] {
  const keys: string[] = [];
  if (values.restrictions === 'false') keys.push('minimal_balance');
  return keys;
}

/**
 * Just-saved delivery values waiting for the by-external-id read to confirm
 * them. The read model can lag behind the vendor PATCH (replica/cache), so a
 * tab-open hydration right after save would otherwise reset the form to the
 * pre-save row. Kept in the query cache per merchant (survives dialog
 * remounts) and cleared once a fresh row confirms the edited fields — or
 * when it expires, so a concurrent edit can never be masked forever.
 */
export interface PendingDeliverySave {
  /** Submitted form values. */
  values: ClientFormValues;
  /** Top-level form fields the save actually changed. */
  keys: (keyof ClientFormValues)[];
  /** `Date.now()` at the successful save. */
  savedAt: number;
}

/** How long a just-saved overlay may mask the by-external-id read. */
export const PENDING_DELIVERY_SAVE_TTL_MS = 5 * 60 * 1000;

export function isPendingDeliverySaveExpired(
  saved: Pick<PendingDeliverySave, 'savedAt'>,
  now: number = Date.now(),
): boolean {
  return now - saved.savedAt > PENDING_DELIVERY_SAVE_TTL_MS;
}

/** Image slots compare by path only (`sequence_number` is transport noise). */
const IMAGE_SLOT_KEYS: ReadonlySet<string> = new Set(['avatar_image', 'business_image']);

/** Numeric fields compare by value — the read may return them as strings. */
const NUMERIC_FIELD_KEYS: ReadonlySet<string> = new Set(['search_time', 'min_balance']);

function imagePaths(value: unknown): unknown[] | null {
  if (!Array.isArray(value)) return null;
  return value.map((item) =>
    typeof item === 'object' && item !== null
      ? (item as { path?: unknown }).path
      : undefined,
  );
}

/**
 * True when the fresh row already carries every just-saved edited field, so
 * the overlay is unnecessary and must be dropped (un-edited concurrent
 * changes then flow through the plain hydration).
 */
export function isPendingDeliverySaveConfirmed(
  fresh: ClientFormValues,
  saved: PendingDeliverySave,
): boolean {
  return saved.keys.every((key) => {
    const a = fresh[key];
    const b = saved.values[key];
    if (IMAGE_SLOT_KEYS.has(key)) {
      const pathsA = imagePaths(a);
      const pathsB = imagePaths(b);
      if (pathsA !== null && pathsB !== null)
        return JSON.stringify(pathsA) === JSON.stringify(pathsB);
    }
    if (NUMERIC_FIELD_KEYS.has(key)) {
      const numA = Number(a);
      const numB = Number(b);
      if (Number.isNaN(numA) && Number.isNaN(numB)) return true;
      return numA === numB;
    }
    return isSamePayloadValue(a, b);
  });
}

/** Stable equality for mapped payload values (Dates, image lists, NaN). */
function isSamePayloadValue(a: unknown, b: unknown): boolean {
  if (a instanceof Date || b instanceof Date) {
    return a instanceof Date && b instanceof Date && a.getTime() === b.getTime();
  }
  if (
    typeof a === 'number' &&
    typeof b === 'number' &&
    Number.isNaN(a) &&
    Number.isNaN(b)
  )
    return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Minimal PATCH payload: the full `mapVendorUpdate` mapping minus
 * disabled (merchant-owned/synced), hidden (never rendered or gated off for
 * the current values) and unchanged (equal to the hydrated vendor baseline)
 * keys. Sharing one `now` between the two mappings keeps the
 * contract-start fallback from looking like a change when unset on both
 * sides. Returns `{}` when there is nothing to send.
 */
export function mapVendorChangedUpdate(args: {
  values: ClientFormValues;
  vendor: DeliveryVendor | undefined;
  now: Date;
}): Record<string, unknown> {
  const { values, vendor, now } = args;
  const full = mapVendorUpdate({ ...values, clientType: 'vendor' }, now) as Record<
    string,
    unknown
  >;
  const baseline = mapVendorUpdate(
    { ...createDeliveryFormDefaultsFromVendor(vendor), clientType: 'vendor' },
    now,
  ) as Record<string, unknown>;
  const skip = new Set<string>([
    ...DISABLED_VENDOR_UPDATE_KEYS,
    ...ALWAYS_HIDDEN_VENDOR_UPDATE_KEYS,
    ...hiddenVendorUpdateKeys(values),
  ]);
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(full)) {
    if (skip.has(key)) continue;
    if (isSamePayloadValue(value, baseline[key])) continue;
    patch[key] = value;
  }
  return patch;
}

/**
 * EditDrawer vendor payload. Avatar/logo take the LAST uploaded image
 * (`length - 1`); `preferred_lang` comes from the form; `allow_multiple_orders`
 * is included (unlike the create mapping). An emptied image slot maps to
 * `null` (never `undefined` — `JSON.stringify` drops `undefined` keys, so a
 * removal would otherwise serialize to `{}` and the backend would keep the
 * old image). The changed-update diff still suppresses the key when both
 * sides are imageless.
 */
export function mapVendorUpdate(data: ClientFormValues, now: Date) {
  return {
    name: data?.first_name,
    logo: data.avatar_image?.[(data?.avatar_image?.length ?? 0) - 1]?.path ?? null,
    preferred_lang: data?.preferred_lang,
    password: data?.password,
    phone: stripPhone(data.phone_number),
    email: data.email,
    minimal_balance: toPayloadMinimalBalance(data?.min_balance),
    enable_balance_restrictions: data?.restrictions === 'true',
    business_number: businessNumber(data?.business_number),
    address: data?.address,
    contract_number: data?.contract_number,
    contract_start_date: data?.contract_start_date ?? now,
    business_desc: data?.buisness_desc,
    business_logo:
      data?.business_image?.[(data?.business_image?.length ?? 0) - 1]?.path ?? null,
    business_name: data?.business_name,
    // Payment method radio (balance vs cash) — edited directly in the tab.
    is_corporate: data?.is_corporate ?? false,
    is_business: data?.is_business,
    door_to_door_required: data?.door_to_door_required,
    allow_multiple_orders: data?.allow_multiple_orders,
    is_client_full_pay: data?.is_client_full_pay ?? false,
    inn: data?.inn,
    official_business_name: data?.official_business_name,
    search_time: Number(data?.search_time),
    // Webhook settings ride the same vendor PATCH as the rest of the tab
    // (empty means unset — sent as null, never as "").
    callback_url: toNullableString(data?.callback_url),
    callback_token: toNullableString(data?.callback_token),
  };
}
